<script>
import Popper from 'popper.js';
import Toolbar from '@/components/ui/Toolbar';

export default {
  name: 'EditorToolbar',
  components: {
    Toolbar
  },
  props: {
    editor: Object
  },
  data() {
    return {
      hasUndo: false,
      hasRedo: false,
      isH1: false,
      isH2: false,
      isH3: false,
      isH4: false,
      isH5: false,
      isH6: false,
      isParagraph: false,
      isPre: false,
      isBlockquote: false,
      isBold: false,
      isItalic: false,
      isStrikethrough: false,
      isSuperscript: false,
      isSubscript: false,
      isCode: false,
      isAnchor: false,
      isCodeBlock: false,
      isHighlight: false,
      isSmall: false,
      isLink: false,
      isGallery: false,
      isImage: false,
      isVideo: false,
      isAlignLeft: false,
      isAlignCenter: false,
      isAlignRight: false,
      isUnorderedList: false,
      isOrderedList: false,
      popper: null,
      position: 'top',
      regionType: 'block'
    };
  },
  computed: {
    buttons() {
      const buttons = [];

      const paragraphButton = {
        icon: 'el-icon-fa-paragraph',
        menu: [
          { text: this.$t('Heading {num}', { num: 1 }), className: 'h1', shortcut: `${this.headingShortcutPrefix}1`, active: this.isH1, click: () => this.editor.toggleFormat('h1') },
          { text: this.$t('Heading {num}', { num: 2 }), className: 'h2', shortcut: `${this.headingShortcutPrefix}2`, active: this.isH2, click: () => this.editor.toggleFormat('h2') },
          { text: this.$t('Heading {num}', { num: 3 }), className: 'h3', shortcut: `${this.headingShortcutPrefix}3`, active: this.isH3, click: () => this.editor.toggleFormat('h3') },
          { text: this.$t('Heading {num}', { num: 4 }), className: 'h4', shortcut: `${this.headingShortcutPrefix}4`, active: this.isH4, click: () => this.editor.toggleFormat('h4') },
          { text: this.$t('Heading {num}', { num: 5 }), className: 'h5', shortcut: `${this.headingShortcutPrefix}5`, active: this.isH5, click: () => this.editor.toggleFormat('h5') },
          { text: this.$t('Heading {num}', { num: 6 }), className: 'h6', shortcut: `${this.headingShortcutPrefix}6`, active: this.isH6, click: () => this.editor.toggleFormat('h6') },
          { text: this.$t('Paragraph'), icon: 'el-icon-fa-paragraph', shortcut: `${this.headingShortcutPrefix}7`, active: this.isParagraph, click: () => this.editor.toggleFormat('p') },
          { text: this.$t('Preformatted Text'), icon: 'el-icon-fa-keyboard', active: this.isPre, click: () => this.editor.toggleFormat('pre') },
          { text: this.$t('Blockquote'), icon: 'el-icon-fa-quote-left', active: this.isBlockquote, click: () => this.editor.toggleFormat('blockquote'), divided: true }
        ]
      };

      const boldButton = {
        icon: 'el-icon-fa-bold',
        active: this.isBold,
        title: this.$t('Bold'),
        click: () => this.editor.toggleFormat('bold')
      };

      const italicButton = {
        icon: 'el-icon-fa-italic',
        active: this.isItalic,
        title: this.$t('Italic'),
        click: () => this.editor.toggleFormat('italic')
      };

      const formatButton = {
        icon: 'el-icon-fa-font',
        menu: [
          { text: this.$t('Bold'), icon: 'el-icon-fa-bold', shortcut: '[Meta]B', active: this.isBold, click: () => this.editor.toggleFormat('bold') },
          { text: this.$t('Italic'), icon: 'el-icon-fa-italic', shortcut: '[Meta]I', active: this.isItalic, click: () => this.editor.toggleFormat('italic') },
          { text: this.$t('Strikethrough'), icon: 'el-icon-fa-strikethrough', active: this.isStrikethrough, click: () => this.editor.toggleFormat('strikethrough') },
          { text: this.$t('Superscript'), icon: 'el-icon-fa-superscript', active: this.isSuperscript, click: () => this.editor.toggleFormat('superscript') },
          { text: this.$t('Subscript'), icon: 'el-icon-fa-subscript', active: this.isSubscript, click: () => this.editor.toggleFormat('subscript') },
          { text: this.$t('Code'), icon: 'el-icon-fa-code', shortcut: '[Shift][Meta]C', active: this.isCode, click: () => this.editor.toggleFormat('code'), divided: true },
          { text: this.$t('Highlight'), icon: 'el-icon-fa-highlighter', active: this.isHighlight, click: () => this.editor.toggleFormat('highlight') },
          { text: this.$t('Small Print'), icon: 'el-icon-fa-font', className: 'small', active: this.isSmall, click: () => this.editor.toggleFormat('small') },
          { text: this.$t('Remove Formatting'), icon: 'el-icon-fa-eraser', click: () => this.editor.removeFormat(), divided: true }
        ],
        title: this.$t('Format')
      };

      const linkButton = {
        icon: 'el-icon-fa-link',
        active: this.isLink,
        title: this.$t('Link'),
        click: () => this.editor.fire('insertLink')
      };

      const imageButton = {
        icon: 'el-icon-fa-image',
        active: this.isImage,
        title: this.$t('Image'),
        click: () => this.editor.fire('insertImage')
      };

      const insertButton = {
        icon: 'el-icon-fa-plus',
        menu: [
          { text: this.$t('Blocks'), icon: 'el-icon-fa-cube', shortcut: '[Shift][Meta]B', click: () => this.editor.fire('insertBlock') },
          { text: this.$t('Link'), icon: 'el-icon-fa-link', shortcut: '[Meta]K', active: this.isLink, click: () => this.editor.fire('insertLink'), divided: true },
          { text: this.$t('Image'), icon: 'el-icon-fa-image', shortcut: '[Shift][Meta]I', active: this.isImage, click: () => this.editor.fire('insertImage') },
          { text: this.$t('Video'), icon: 'el-icon-fa-video', active: this.isVideo, click: () => this.editor.fire('insertEmbed') },
          { text: this.$t('Table'), icon: 'el-icon-fa-table', click: () => this.editor.fire('insertTable') },
          { text: this.$t('Anchor'), icon: 'el-icon-fa-bookmark', shortcut: '[Shift][Meta]A', active: this.isAnchor, click: () => this.editor.fire('insertAnchor'), divided: true },
          { text: this.$t('Code Block'), icon: 'el-icon-fa-code', active: this.isCodeBlock, click: () => this.editor.fire('insertCodeBlock') },
          { text: this.$t('Gallery'), icon: 'el-icon-fa-images', active: this.isGallery, click: () => this.editor.fire('insertGallery') },
          { text: this.$t('Section Break'), iconText: '§', shortcut: '[Meta]Enter', click: () => this.editor.insertContent('<hr>') }
        ],
        title: this.$t('Insert')
      };

      const alignmentButton = {
        icon: 'el-icon-fa-align-left',
        menu: [
          { text: this.$t('Align Left'), icon: 'el-icon-fa-align-left', active: this.isAlignLeft, click: () => this.editor.toggleAlignment('left') },
          { text: this.$t('Align Center'), icon: 'el-icon-fa-align-center', active: this.isAlignCenter, click: () => this.editor.toggleAlignment('center') },
          { text: this.$t('Align Right'), icon: 'el-icon-fa-align-right', active: this.isAlignRight, click: () => this.editor.toggleAlignment('right') }
        ],
        title: this.$t('Alignment')
      };

      const listsButton = {
        icon: 'el-icon-fa-list-ul',
        menu: [
          { text: this.$t('Bulleted List'), icon: 'el-icon-fa-list-ul', active: this.isUnorderedList, click: () => this.editor.toggleUnorderedList() },
          { text: this.$t('Numbered List'), icon: 'el-icon-fa-list-ol', active: this.isOrderedList, click: () => this.editor.toggleOrderedList() },
          { text: this.$t('Increase Indent'), icon: 'el-icon-fa-indent', divided: true, click: () => this.editor.indent() },
          { text: this.$t('Decrease Indent'), icon: 'el-icon-fa-outdent', click: () => this.editor.outdent() }
        ],
        title: this.$t('Lists')
      };

      const undoButton = {
        icon: 'el-icon-fa-undo',
        disabled: !this.hasUndo,
        title: this.$t('Undo'),
        click: () => this.editor.undo()
      };

      const redoButton = {
        icon: 'el-icon-fa-redo',
        disabled: !this.hasRedo,
        title: this.$t('Redo'),
        click: () => this.editor.redo()
      };

      if (this.regionType === 'block') {
        buttons.push(
          [paragraphButton],
          [boldButton, italicButton, formatButton],
          [linkButton, imageButton, insertButton],
          [alignmentButton, listsButton],
          [undoButton, redoButton]
        );
      }

      if (this.regionType === 'inline' || this.regionType === 'link') {
        buttons.push(
          [boldButton, italicButton, formatButton],
          [linkButton],
          [undoButton, redoButton]
        );
      }

      return buttons;
    },
    headingShortcutPrefix() {
      const isMac = !!navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i);
      return isMac ? '[Ctrl][Alt]' : '[Alt][Shift]';
    }
  },
  watch: {
    editor() {
      if (!this.editor) return;
      if (!this.popper) {
        this.popper = new Popper(this.editor.getRootElement(), this.$refs.toolbar.$el, {
          placement: 'top',
          removeOnDestroy: true,
          modifiers: {
            flip: {
              enabled: true
            },
            offset: {
              enabled: true,
              offset: '0px, 16px'
            },
            preventOverflow: {
              enabled: true
            },
            hide: {
              enabled: true
            }
          },
          onUpdate: data => {
            this.position = data.placement === 'bottom' ? 'bottom' : 'top';
          }
        });
      } else {
        this.popper.reference = this.editor.getRootElement();
        this.popper.scheduleUpdate();
      }

      // Set region type
      this.regionType = this.editor.getRegionType();

      // Listen for selection change on the editor
      this.editor.off('change.editorToolbar');
      this.editor.on('change.editorToolbar', () => this.handleSelectionChange());
      this.handleSelectionChange();
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.cancelMouseDown);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.cancelMouseDown);
  },
  methods: {
    // Prevents toolbar clicks from removing focus from the editor
    cancelMouseDown(event) {
      if (event.target.closest('.toolbar, .el-dropdown-menu')) {
        event.preventDefault();
      }
    },

    // Updates the toolbar when the selection changes (e.g. on NodeChange)
    handleSelectionChange() {
      const editor = this.editor;
      if (!editor) return;
      this.popper.scheduleUpdate();
      this.isH1 = editor.hasFormat('h1');
      this.isH2 = editor.hasFormat('h2');
      this.isH3 = editor.hasFormat('h3');
      this.isH4 = editor.hasFormat('h4');
      this.isH5 = editor.hasFormat('h5');
      this.isH6 = editor.hasFormat('h6');
      this.isParagraph = editor.hasFormat('p');
      this.isPre = editor.hasFormat('pre');
      this.isBlockquote = editor.hasFormat('blockquote');
      this.isBold = editor.hasFormat('bold');
      this.isItalic = editor.hasFormat('italic');
      this.isStrikethrough = editor.hasFormat('strikethrough');
      this.isSuperscript = editor.hasFormat('superscript');
      this.isSubscript = editor.hasFormat('subscript');
      this.isCode = editor.hasFormat('code');
      this.isAnchor = editor.isAnchor();
      this.isCodeBlock = editor.isCodeBlock();
      this.isHighlight = editor.hasFormat('highlight');
      this.isSmall = editor.hasFormat('small');
      this.isLink = editor.isLink();
      this.isGallery = editor.isGallery();
      this.isImage = editor.isImage();
      this.isVideo = editor.isEmbed();
      this.isAlignLeft = editor.isAlignLeft();
      this.isAlignCenter = editor.isAlignCenter();
      this.isAlignRight = editor.isAlignRight();
      this.isUnorderedList = editor.isUnorderedList();
      this.isOrderedList = editor.isOrderedList();
      this.hasUndo = editor.hasUndo();
      this.hasRedo = editor.hasRedo();
    }
  }
};
</script>

<template>
  <toolbar
    ref="toolbar"
    :hidden="!editor || editor.options.plainText"
    :position="position"
    :buttons="buttons"
    class="editor-toolbar"
  />
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.editor-toolbar {
  position: absolute;

  @include breakpoint('sm-and-down') {
    width: 100%;
    transform: none !important;
    border-radius: 0;
    white-space: nowrap;
    overflow: auto;

    &::after,
    &::before {
      display: none;
    }
  }

  .small i {
    font-size: .5rem;
  }
}
</style>

